import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useDocs } from "@/lib/DocsContext";
import { extractHeadings, LANG_BY_TYPE } from "@/lib/docs";
import { ApprovalBanner } from "@/components/docs/ApprovalBanner";
import { DocHeader } from "@/components/docs/DocHeader";
import { MarkdownView } from "@/components/docs/MarkdownView";
import { CodeBlock } from "@/components/docs/CodeBlock";
import { TableOfContents } from "@/components/docs/TableOfContents";

const Pager = ({ prev, next }) => (
  <nav className="mt-16 grid gap-3 border-t border-border pt-6 sm:grid-cols-2" data-testid="doc-pager">
    {prev ? (
      <Link to={`/doc/${prev.path}`} data-testid="doc-pager-prev-link" className="group rounded-lg border border-border p-4 transition-colors hover:border-primary/50 hover:bg-elevated">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><ArrowLeft className="h-3 w-3 transition-transform group-hover:-translate-x-0.5" /> Prethodno</span>
        <span className="mt-1 block text-sm font-medium">{prev.title}</span>
      </Link>
    ) : <span />}
    {next && (
      <Link to={`/doc/${next.path}`} data-testid="doc-pager-next-link" className="group rounded-lg border border-border p-4 text-right transition-colors hover:border-primary/50 hover:bg-elevated">
        <span className="flex items-center justify-end gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Sljedeće <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></span>
        <span className="mt-1 block text-sm font-medium">{next.title}</span>
      </Link>
    )}
  </nav>
);

const Message = ({ testId, children }) => (
  <div className="mx-auto max-w-3xl px-6 py-24 text-center text-muted-foreground" data-testid={testId}>{children}</div>
);

export default function DocPage() {
  const path = useParams()["*"];
  const { docs, texts, failed, manifest } = useDocs();
  const [mode, setMode] = useState("rendered");
  const [lineNumbers, setLineNumbers] = useState(true);
  const index = docs.findIndex((d) => d.path === path);
  const doc = docs[index];
  const text = texts[path];
  const headings = useMemo(() => (doc?.type === "md" && text ? extractHeadings(text) : []), [doc, text]);

  useEffect(() => {
    setMode("rendered");
    if (!window.location.hash) window.scrollTo({ top: 0 });
  }, [path]);

  if (!manifest) return <Message testId="doc-loading">Učitavanje paketa…</Message>;
  if (!doc) return <Message testId="doc-not-found">Dokument <code>{path}</code> nije dio G0 paketa.</Message>;
  if (failed[path]) return <Message testId="doc-load-error">Dokument se nije mogao učitati.</Message>;
  if (text === undefined) return <Message testId="doc-loading">Učitavanje dokumenta…</Message>;

  const isMd = doc.type === "md";

  return (
    <div className="flex">
      <article className="mx-auto w-full min-w-0 max-w-5xl px-4 py-8 sm:px-6 lg:px-10" data-testid="doc-page">
        <div className="mb-8">
          <ApprovalBanner compact />
        </div>
        <DocHeader doc={doc} text={text} mode={mode} setMode={setMode} lineNumbers={lineNumbers} setLineNumbers={setLineNumbers} />
        {isMd && mode === "rendered" ? (
          <MarkdownView text={text} />
        ) : (
          <CodeBlock code={text} language={LANG_BY_TYPE[doc.type]} showLineNumbers={isMd || lineNumbers} testId="doc-source" bare />
        )}
        <Pager prev={docs[index - 1]} next={docs[index + 1]} />
      </article>
      {isMd && mode === "rendered" && (
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 overflow-y-auto border-l border-border p-5 xl:block">
          <TableOfContents headings={headings} />
        </aside>
      )}
    </div>
  );
}
